import React, { useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Controller } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
// Deprecated component removed: CountryCityInput
import CountriesSelect from './components/CountriesSelect';
import CitiesSelect from './components/CitiesSelect';
import UsersSelect from './components/UsersSelect';
import PlacesSelect from './components/PlacesSelect';
import { tripCreatorSchema, transportModes, companionshipEnum, softValidateLocationsAgainstDuration } from './schema';

export default function TripCreatorForm({ onCreate }) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(tripCreatorSchema),
    defaultValues: {
      durationDays: '',
      budget: '',
      startDate: '',
      companionship: '',
  locations: [], // deprecated
  countries: [],
  cities: [],
      mustVisit: [],
      excludePlaces: [],
      transport: [],
      minSleepHours: '',
      companions: [],
    },
    mode: 'onBlur',
  });

  const durationDays = watch('durationDays');
  const countries = watch('countries');
  const cities = watch('cities');

  // TODO: optionally reintroduce soft warnings based on countries + cities counts
  const softWarnings = null;

  const onSubmit = (data) => {
    // Normalize date to ISO string for downstream APIs
    const payload = {
      ...data,
      startDate: data.startDate ? new Date(data.startDate).toISOString() : undefined,
      countriesIds: (data.countries || []).map((c) => c.id),
      cityIds: (data.cities || []).map((c) => c.id),
      mustVisitIds: (data.mustVisit || []).map((p) => p.id),
      excludePlaceIds: (data.excludePlaces || []).map((p) => p.id),
      companionIds: (data.companions || []).map((u) => u.id),
    };
    if (typeof onCreate === 'function') onCreate(payload);
    // Fallback: simple log for now
    // eslint-disable-next-line no-console
    console.log('Trip preferences:', payload);
  };

  const toggleTransport = (mode) => {
    const current = watch('transport') || [];
    const next = current.includes(mode)
      ? current.filter((m) => m !== mode)
      : [...current, mode];
    setValue('transport', next, { shouldValidate: true });
  };

  return (
    <div className="max-w-xl mx-auto p-4 sm:p-6">
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">Create a Trip</h1>
      <p className="text-sm text-gray-600 mb-2">Fill what you can. Either Duration or Budget is required.</p>
      <p className="text-sm text-indigo-600 mb-4">Tip: pick a start date and invite friends — make it an adventure ✨</p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <section className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs font-medium text-gray-700 mb-1">Duration</label>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                placeholder="Days"
                {...register('durationDays')}
              />
              {errors.durationDays && (
                <p className="text-[11px] text-red-600 mt-1">{errors.durationDays.message}</p>
              )}
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs font-medium text-gray-700 mb-1">Budget</label>
              <input
                type="number"
                inputMode="decimal"
                min={1}
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                placeholder="Total"
                {...register('budget')}
              />
              {errors.budget && <p className="text-[11px] text-red-600 mt-1">{errors.budget.message}</p>}
            </div>
          </div>
          {errors.root?.message && (
            <p className="text-xs text-red-600">{errors.root.message}</p>
          )}
        </section>

        <section className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-1">Start Date</label>
            <Controller
              control={control}
              name="startDate"
              render={({ field }) => (
                <DatePicker
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  placeholderText="Choose a start date"
                  selected={field.value ? (field.value instanceof Date ? field.value : new Date(field.value)) : null}
                  onChange={(date) => field.onChange(date)}
                  onBlur={field.onBlur}
                  dateFormat="yyyy-MM-dd"
                  isClearable
                  todayButton="Today"
                  minDate={new Date()}
                  // Mobile friendly: use a popper placement that works on small screens
                  popperPlacement="bottom"
                />
              )}
            />
          </div>


          <CountriesSelect
            value={countries}
            onChange={(v) => setValue('countries', v, { shouldValidate: true })}
          />
          <CitiesSelect
            countries={countries}
            value={cities}
            onChange={(v) => setValue('cities', v, { shouldValidate: true })}
          />


          <UsersSelect
            value={watch('companions')}
            onChange={(v) => setValue('companions', v, { shouldValidate: true })}
            max={20}
          />
        </section>

        <section className="pt-2 border-t border-gray-100">
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300"
              checked={showAdvanced}
              onChange={(e) => setShowAdvanced(e.target.checked)}
            />
            Show advanced options
          </label>

          {showAdvanced && (
            <div className="mt-3 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-1">Companionship</label>
                <select
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
                  {...register('companionship')}
                >
                  <option value="">Select</option>
                  {companionshipEnum.options.map((o) => (
                    <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>
                  ))}
                </select>
                {errors.companionship && (
                  <p className="text-xs text-red-600 mt-1">{errors.companionship.message}</p>
                )}
              </div>

              <PlacesSelect
                label="Must-visit places"
                value={watch('mustVisit')}
                onChange={(v) => setValue('mustVisit', v, { shouldValidate: true })}
                countries={countries}
                cities={cities}
              />
              <PlacesSelect
                label="Exclude places"
                value={watch('excludePlaces')}
                onChange={(v) => setValue('excludePlaces', v, { shouldValidate: true })}
                countries={countries}
                cities={cities}
              />

              <div>
                <label className="block text-sm font-medium text-gray-800 mb-1">Preferred transport</label>
                <div className="flex flex-wrap gap-2">
                  {transportModes.map((mode) => {
                    const selected = (watch('transport') || []).includes(mode);
                    return (
                      <button
                        key={mode}
                        type="button"
                        className={
                          'px-3 py-1 rounded-full text-sm border ' +
                          (selected ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-800 border-gray-200')
                        }
                        onClick={() => toggleTransport(mode)}
                      >
                        {mode}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-800 mb-1">Minimum sleep (hours)</label>
                <input
                  type="number"
                  min={0}
                  max={14}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  placeholder="e.g. 7"
                  {...register('minSleepHours')}
                />
                {errors.minSleepHours && (
                  <p className="text-xs text-red-600 mt-1">{errors.minSleepHours.message}</p>
                )}
              </div>
            </div>
          )}
        </section>

        <div className="pt-2">
          <button
            type="submit"
            className="w-full sm:w-auto inline-flex items-center justify-center rounded-lg bg-gray-900 text-white px-4 py-2 text-sm font-medium shadow-sm hover:bg-black focus:outline-none"
          >
            Get Recommendations
          </button>
        </div>
      </form>
    </div>
  );
}
